import React from 'react';
import { ChevronRight, Home } from 'lucide-react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import styles from './Breadcrumbs.module.css';

const Breadcrumbs = ({ crumbs: manualCrumbs, customItems }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // If custom items are provided, use them
  if (customItems && customItems.length > 0) {
    return (
      <div className={styles['breadcrumb-container']}>
        <div 
          className={[styles['breadcrumb-item'], styles['clickable']].join(' ')} 
          onClick={() => navigate('/')}
        >
          <Home size={14} />
        </div>
        <ChevronRight className={styles['breadcrumb-separator']} size={14} />
        
        {customItems.map((item, index) => {
          const isLast = index === customItems.length - 1;
          return (
            <React.Fragment key={index}>
              <span 
                className={`${styles['breadcrumb-item']} ${isLast ? styles['current'] : (item.path ? styles['clickable'] : '')}`}
                onClick={() => !isLast && item.path && navigate(item.path)}
              >
                {item.label}
              </span>
              {!isLast && (
                <ChevronRight className={styles['breadcrumb-separator']} size={14} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // If manual crumbs are provided, use them (backward compatibility)
  if (manualCrumbs && manualCrumbs.length > 0) {
    return (
      <div className={styles['breadcrumb-container']}>
        {manualCrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            <span className={`${styles['breadcrumb-item']} ${index === manualCrumbs.length - 1 ? styles['current'] : ''}`}>
              {crumb}
            </span>
            {index < manualCrumbs.length - 1 && (
              <ChevronRight className={styles['breadcrumb-separator']} size={14} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // Dynamic breadcrumb logic
  const pathnames = location.pathname.split('/').filter((x) => x);
  
  const breadcrumbMap = {
    'projects': 'Projects',
    'create': 'Create',
    'purchase-orders': 'Purchase Orders',
    'vendors': 'Vendors',
    'pos': 'LOIs',
    'work-orders': 'Work Orders',
    'invoices': 'Sales Invoices',
    'finance': 'Finance',
    'payments': 'Payments',
    'payment': 'Payment',
    'vendor-payments': 'Vendor Payments',
    'inspections': 'Inspections',
    'schedule': 'Schedule',
    'clearance': 'Clearance',
    'receipts': 'Material Receipts',
    'add': 'Add',
    'edit': 'Edit',
    'view': 'View',
    'upload': 'Upload',
    'review': 'Review',
    'customers': 'Customers',
    'units': 'Units',
    'taxes': 'Taxes',
    'project-types': 'Project Types',
    'banks': 'Banks',
    'employees': 'Employees',
    'departments': 'Departments',
    'designations': 'Designations',
    'users': 'Users',
    'user-permissions': 'User Permissions',
    'design-submissions': 'Design Submissions',
    'design-approval': 'Design Approval',
    'settings': 'Settings',
    'profile': 'Profile',
    'masters': 'Masters',
    'holidays': 'Holidays',
    'leave-types': 'Leave Types',
    'dashboard': 'Dashboard'
  };

  const getLabel = (path) => {
    // If it's a known path, use mapping
    if (breadcrumbMap[path]) return breadcrumbMap[path];
    
    // If it looks like an ID like PRJ-001 or PO-2023-01, keep it as is
    if (/^[A-Z]{2,}-/.test(path)) {
      return path;
    }
    
    // For other paths, replace hyphens with spaces and capitalize each word
    return path
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Dynamic display parsing
  
  const displayItems = [];
  let currentPath = '';

  pathnames.forEach((name) => {
    currentPath += `/${name}`;
    // Identify if the segment is a numeric ID or a UUID
    const isId = /^\d+$/.test(name) || /^[0-9a-fA-F-]{36}$/.test(name);
    
    if (!isId) {
      displayItems.push({
        label: getLabel(name),
        path: currentPath
      });
    } else {
      // If it is an ID, update the path of the previous breadcrumb to include this ID
      // This ensures if it were clickable, it points to the correct ID route.
      if (displayItems.length > 0) {
        displayItems[displayItems.length - 1].path = currentPath;
      }
    }
  });

  return (
    <div className={styles['breadcrumb-container']}>
      <div 
        className={[styles['breadcrumb-item'], styles['clickable']].join(' ')} 
        onClick={() => navigate('/')}
      >
        <Home size={14} />
      </div>
      
      {displayItems.length > 0 && <ChevronRight className={styles['breadcrumb-separator']} size={14} />}

      {displayItems.map((item, index) => {
        const isLast = index === displayItems.length - 1;

        return (
          <React.Fragment key={index}>
            <span 
              className={`${styles['breadcrumb-item']} ${isLast ? styles['current'] : styles['clickable']}`}
              onClick={() => !isLast && navigate(item.path)}
            >
              {item.label}
            </span>
            {!isLast && (
              <ChevronRight className={styles['breadcrumb-separator']} size={14} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Breadcrumbs;
